import { useEffect, useState } from 'react';
import {
    Alert,
    Anchor,
    Badge,
    Button,
    Card,
    CopyButton,
    Drawer,
    Group,
    SimpleGrid,
    Stack,
    Tabs,
    Text,
    Title,
    UnstyledButton,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Copy, DoorOpen, Download, ExternalLink, RefreshCw, Search } from 'lucide-react';
import { api } from '@/api/client';
import { LOCKER_STATE_META, lockerDetailKey, lockerState, useLockersHealth } from '@/api/lockers';
import type { CellRow, LockerDetail } from '@/api/types';
import { QueryState } from '@/components/Page';
import { QrCode, qrToPngBlob } from '@/components/QrCode';
import { formatMsk, timeAgo } from '@/lib/format';
import { notifyError, notifySuccess } from '@/lib/notify';
import { LEGACY_ADMIN_URL } from '@/navigation';

function CellTile({ cell, onClick }: { cell: CellRow; onClick: () => void }) {
    return (
        <UnstyledButton
            onClick={onClick}
            aria-label={`Ячейка ${cell.cell_number}`}
            style={{
                aspectRatio: '1',
                borderRadius: 'var(--mantine-radius-md)',
                border: `2px solid ${cell.is_open ? 'var(--mantine-color-yellow-filled)' : 'var(--mantine-color-default-border)'}`,
                background: 'var(--mantine-color-default)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
            }}
        >
            <Text fw={800} size="sm" lh={1}>
                {cell.cell_number}
            </Text>
            <Text size="lg" lh={1}>
                {cell.is_open ? '🔓' : cell.emoji || '·'}
            </Text>
        </UnstyledButton>
    );
}

function CellDrawer({ lockerId, cell, onClose }: { lockerId: string; cell: CellRow | null; onClose: () => void }) {
    const queryClient = useQueryClient();
    const [doorState, setDoorState] = useState<boolean | null>(null);

    useEffect(() => setDoorState(null), [cell?.cell_number]);

    const open = useMutation({
        mutationFn: () => api.post('/api/admin/cells/open', { locker_id: lockerId, cell_number: cell!.cell_number }),
        onSuccess: () => {
            notifySuccess(`Команда на открытие ячейки ${cell!.cell_number} отправлена`);
            setTimeout(() => queryClient.invalidateQueries({ queryKey: lockerDetailKey(lockerId) }), 1500);
        },
        onError: notifyError,
    });

    const check = useMutation({
        mutationFn: () =>
            api.post<{ is_open: boolean }>('/api/admin/cells/check', { locker_id: lockerId, cell_number: cell!.cell_number }),
        onSuccess: (r) => setDoorState(!!r.is_open),
        onError: notifyError,
    });

    const confirmOpen = () =>
        modals.openConfirmModal({
            title: `Открыть ячейку ${cell?.cell_number}?`,
            children: <Text size="sm">Дверца откроется физически. Бизнес-статус ячейки не меняется.</Text>,
            labels: { confirm: 'Открыть', cancel: 'Отмена' },
            onConfirm: () => open.mutate(),
        });

    const door = doorState ?? cell?.is_open;

    return (
        <Drawer
            opened={!!cell}
            onClose={onClose}
            position="bottom"
            title={`Ячейка ${cell?.cell_number ?? ''}`}
            radius="lg"
            // Шторка по высоте содержимого, а не на весь экран.
            styles={{ content: { height: 'auto', maxHeight: '90dvh' } }}
        >
            {cell && (
                <Stack pb="md">
                    <SimpleGrid cols={2}>
                        <Card padding="sm">
                            <Text size="xs" c="dimmed">
                                Статус
                            </Text>
                            <Text fw={700}>
                                {cell.emoji} {cell.status_name} <Text span c="dimmed" size="sm">#{cell.status_id}</Text>
                            </Text>
                        </Card>
                        <Card padding="sm">
                            <Text size="xs" c="dimmed">
                                Дверца {doorState !== null && '(проверено)'}
                            </Text>
                            <Text fw={700} c={door ? 'yellow' : undefined}>
                                {door ? '🔓 открыта' : '🔒 закрыта'}
                            </Text>
                        </Card>
                    </SimpleGrid>
                    {cell.current_bag_id && (
                        <Text size="sm" c="dimmed">
                            Мешок в ячейке: #{cell.current_bag_id}
                        </Text>
                    )}
                    <Group grow>
                        <Button leftSection={<DoorOpen size={16} />} onClick={confirmOpen} loading={open.isPending}>
                            Открыть
                        </Button>
                        <Button variant="default" leftSection={<Search size={16} />} onClick={() => check.mutate()} loading={check.isPending}>
                            Проверить
                        </Button>
                    </Group>
                    <Anchor
                        href={`${LEGACY_ADMIN_URL}/locker/${lockerId}`}
                        target="_blank"
                        size="sm"
                        c="dimmed"
                    >
                        Смена статуса и история — в старой админке <ExternalLink size={12} />
                    </Anchor>
                </Stack>
            )}
        </Drawer>
    );
}

function CellsTab({ lockerId }: { lockerId: string }) {
    const [selected, setSelected] = useState<number | null>(null);
    const detail = useQuery({
        queryKey: lockerDetailKey(lockerId),
        queryFn: () => api.get<LockerDetail>(`/api/admin/lockers/${encodeURIComponent(lockerId)}`),
    });
    const cells = detail.data?.cells ?? [];
    const openCount = cells.filter((c) => c.is_open).length;

    return (
        <QueryState isLoading={detail.isLoading} error={detail.error} onRetry={detail.refetch}>
            <Stack>
                {openCount > 0 && (
                    <Alert color="yellow" variant="light">
                        Открыто ячеек: {openCount}
                    </Alert>
                )}
                <SimpleGrid cols={{ base: 6, xs: 8, sm: 10, lg: 12 }} spacing={8}>
                    {cells.map((c) => (
                        <CellTile key={c.cell_number} cell={c} onClick={() => setSelected(c.cell_number)} />
                    ))}
                </SimpleGrid>
            </Stack>
            <CellDrawer
                lockerId={lockerId}
                cell={cells.find((c) => c.cell_number === selected) ?? null}
                onClose={() => setSelected(null)}
            />
        </QueryState>
    );
}

interface QrInfo {
    qr_url: string | null;
    expires_at?: string | null;
    ttl_remaining_ms?: number | null;
}

function QrTab({ lockerId }: { lockerId: string }) {
    const qr = useQuery({
        queryKey: ['locker-qr', lockerId],
        queryFn: () => api.get<QrInfo>(`/api/admin/lockers/${encodeURIComponent(lockerId)}/qr`),
    });
    const refresh = useMutation({
        mutationFn: () => api.post(`/api/admin/lockers/${encodeURIComponent(lockerId)}/refresh-qr`),
        onSuccess: () => {
            notifySuccess('Новый QR выдан постамату');
            qr.refetch();
        },
        onError: notifyError,
    });

    const download = async (url: string) => {
        const blob = await qrToPngBlob(url);
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `qr-${lockerId}.png`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    return (
        <QueryState isLoading={qr.isLoading} error={qr.error} onRetry={qr.refetch}>
            {qr.data?.qr_url ? (
                <Card maw={720}>
                    <Group align="flex-start" gap="xl">
                        <QrCode value={qr.data.qr_url} />
                        <Stack gap="sm" style={{ flex: 1, minWidth: 240 }}>
                            <Text size="xs" c="dimmed">
                                Ссылка из QR
                            </Text>
                            <Text size="sm" ff="monospace" style={{ wordBreak: 'break-all' }}>
                                {qr.data.qr_url}
                            </Text>
                            <Text size="sm" c="dimmed">
                                Действует до {formatMsk(qr.data.expires_at)}
                            </Text>
                            <Group gap="xs">
                                <CopyButton value={qr.data.qr_url}>
                                    {({ copied, copy }) => (
                                        <Button size="xs" variant="light" leftSection={<Copy size={14} />} onClick={copy}>
                                            {copied ? 'Скопировано' : 'Ссылка'}
                                        </Button>
                                    )}
                                </CopyButton>
                                <Button size="xs" variant="light" leftSection={<Download size={14} />} onClick={() => download(qr.data!.qr_url!)}>
                                    PNG
                                </Button>
                                <Button
                                    size="xs"
                                    variant="default"
                                    leftSection={<RefreshCw size={14} />}
                                    loading={refresh.isPending}
                                    onClick={() => refresh.mutate()}
                                >
                                    Новый QR
                                </Button>
                            </Group>
                        </Stack>
                    </Group>
                </Card>
            ) : (
                <Alert title="QR нет" maw={520}>
                    <Stack gap="sm" align="flex-start">
                        Постамат не на связи или ещё не просканировал ячейки — токен выдаётся после скана.
                        <Button size="xs" variant="light" onClick={() => qr.refetch()} loading={qr.isFetching}>
                            Проверить ещё раз
                        </Button>
                    </Stack>
                </Alert>
            )}
        </QueryState>
    );
}

export function LockerDetailScreen() {
    const { lockerId = '' } = useParams();
    const navigate = useNavigate();
    const health = useLockersHealth();
    const locker = health.data?.find((l) => l.id === lockerId || String(l.id_db) === lockerId);
    const state = locker ? lockerState(locker) : null;

    return (
        <Stack gap="lg" maw={1280} mx="auto">
            <Group gap="sm" wrap="nowrap">
                <Button variant="subtle" color="gray" px="xs" onClick={() => navigate('/lockers')} aria-label="Назад">
                    <ArrowLeft size={20} />
                </Button>
                <Title order={2} fz={{ base: 22, sm: 26 }} style={{ flex: 1 }}>
                    {locker?.display_name || lockerId}
                </Title>
                {state && <Badge color={LOCKER_STATE_META[state].color}>{LOCKER_STATE_META[state].label}</Badge>}
            </Group>

            {locker && (
                <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
                    {[
                        ['Локация', locker.location_name || '—'],
                        ['Ячеек', locker.total_cells ?? '—'],
                        ['Версия', locker.version_name ? `v${locker.version_name} (${locker.version_code ?? '—'})` : '—'],
                        ['На связи', timeAgo(locker.last_online_at)],
                    ].map(([label, value]) => (
                        <Card key={label} padding="sm">
                            <Text size="xs" c="dimmed">
                                {label}
                            </Text>
                            <Text fw={700} size="sm">
                                {value}
                            </Text>
                        </Card>
                    ))}
                </SimpleGrid>
            )}

            <Tabs defaultValue="cells" keepMounted={false}>
                <Tabs.List mb="md">
                    <Tabs.Tab value="cells">Ячейки</Tabs.Tab>
                    <Tabs.Tab value="qr">QR</Tabs.Tab>
                    <Tabs.Tab value="more">Ещё</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="cells">
                    <CellsTab lockerId={lockerId} />
                </Tabs.Panel>
                <Tabs.Panel value="qr">
                    <QrTab lockerId={lockerId} />
                </Tabs.Panel>
                <Tabs.Panel value="more">
                    <Alert title="Пока в старой админке" maw={520}>
                        <Stack gap="sm" align="flex-start">
                            Продукты и правила постамата, даты без возврата, настройки и история ячеек.
                            <Button
                                component="a"
                                href={`${LEGACY_ADMIN_URL}/locker/${lockerId}`}
                                target="_blank"
                                rel="noopener"
                                variant="light"
                                rightSection={<ExternalLink size={14} />}
                            >
                                Открыть
                            </Button>
                        </Stack>
                    </Alert>
                </Tabs.Panel>
            </Tabs>
        </Stack>
    );
}
